import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Truck, CheckCircle2, AlertCircle, Weight,
  Plus, FileBarChart2, RefreshCw, X, Upload
} from 'lucide-react';
import Layout from '../Layout';
import AutocompleteInput from '../AutocompleteInput';
import { fetchVehicles, createVehicleEntry, submitVehicleExit, fetchCharaTypes, uploadFile } from '../../api';
import { useNavigate } from 'react-router-dom';

/* ── helpers ─────────────────────────────────────────────── */
const today   = () => new Date().toISOString().split('T')[0];
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const calcNet = (entryW, exitW) => {
  const a = parseFloat(entryW), b = parseFloat(exitW);
  if (!a || !b || b === 0) return null;
  return (a - b).toFixed(2);
};

/* ── Tooltip ─────────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:'#fff', border:'1px solid #E2E8F0',
      borderRadius:8, padding:'8px 14px', fontSize:13,
      boxShadow:'0 4px 12px rgba(0,0,0,.10)',
    }}>
      <div style={{ fontWeight:700, marginBottom:2, color:'#1A202C' }}>{label}</div>
      <div style={{ color:'#1E5FA8' }}>
        Net Chara: <strong>{payload[0]?.value?.toFixed(2)} kg</strong>
      </div>
    </div>
  );
};

/* ── FileZone ────────────────────────────────────────────── */
function FileZone({ value, onChange, id, required }) {
  return (
    <div className="file-upload-zone">
      <input type="file" id={id} accept="image/*" required={required} onChange={e => onChange(e.target.files[0] || null)} />
      {value
        ? <span className="upload-name"><Upload size={12} style={{ marginRight:4 }} />{value.name}</span>
        : <span className="upload-text"><Upload size={12} style={{ marginRight:4 }} />Click to upload weight slip</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
function Homepage({ userRole, userEmail }) {
  const navigate = useNavigate();

  const [entries,    setEntries]    = useState([]);
  const [charaTypes, setCharaTypes] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [chartDays,  setChartDays]  = useState(30);
  const [entryModal, setEntryModal] = useState(false);
  const [exitModal,  setExitModal]  = useState(false);

  const [entryForm, setEntryForm] = useState({
    date:'', vehicleNo:'', vehicleWeight:'',
    entryTime:'', driverName:'', driverContact:'',
    charaType:'', entryWeightSlip: null, vehicleImage: null,
  });
  const [exitForm, setExitForm] = useState({
    exitDate:'', exitVehicleNo:'', exitTime:'',
    exitVehicleWeight:'', charaType:'', exitWeightSlip: null,
  });

  /* ── autocomplete pools ─────────────────────────────────── */
  const vehicleNoSugs     = useMemo(() => [...new Set(entries.map(e => e.vehicleNo).filter(Boolean))],     [entries]);
  const driverNameSugs    = useMemo(() => [...new Set(entries.map(e => e.driverName).filter(Boolean))],    [entries]);
  const driverContactSugs = useMemo(() => [...new Set(entries.map(e => e.driverContact).filter(Boolean))],[entries]);

  /* ── map DB row ─────────────────────────────────────────── */
  const mapRow = (r) => ({
    id:                  r.id,
    date:                r.date,
    vehicleNo:           r.vehicle_no,
    vehicleWeight:       r.vehicle_weight,
    entryTime:           r.entry_time,
    driverName:          r.driver_name,
    driverContact:       r.driver_contact,
    charaType:           r.chara_type || '',
    entryWeightSlipUrl:  r.entry_weight_slip_url || null,
    vehicleImageUrl:     r.vehicle_image_url || null,
    exitDate:            r.exit_date || '',
    exitTime:            r.exit_time || '',
    exitVehicleWeight:   r.exit_vehicle_weight || '',
    exitWeightSlipUrl:   r.exit_weight_slip_url || null,
    exited:              r.exited || false,
    createdAt:           r.created_at,
  });

  /* ── load ───────────────────────────────────────────────── */
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchVehicles();
      setEntries(data.map(mapRow));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const loadCharaTypes = useCallback(async () => {
    try {
      const { data } = await fetchCharaTypes();
      setCharaTypes(data.map(d => d.name));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadEntries(); loadCharaTypes(); }, [loadEntries, loadCharaTypes]);

  /* ── modal open → auto-fill ─────────────────────────────── */
  useEffect(() => {
    if (entryModal) setEntryForm(p => ({ ...p, date: today(), entryTime: nowTime() }));
  }, [entryModal]);
  useEffect(() => {
    if (exitModal)  setExitForm(p  => ({ ...p, exitDate: today(), exitTime: nowTime() }));
  }, [exitModal]);
  useEffect(() => {
    document.body.style.overflow = (entryModal || exitModal) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [entryModal, exitModal]);

  /* ── upload ─────────────────────────────────────────────── */
  const uploadSlip = async (file, folder) => {
    if (!file) return null;
    return uploadFile(file, folder);
  };

  /* ── entry submit ───────────────────────────────────────── */
  const handleEntrySubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const slipUrl       = await uploadSlip(entryForm.entryWeightSlip, 'entry');
      const vehicleImgUrl = await uploadSlip(entryForm.vehicleImage, 'vehicle');
      const { data } = await createVehicleEntry({
        date:                entryForm.date,
        vehicleNo:           entryForm.vehicleNo,
        vehicleWeight:       parseFloat(entryForm.vehicleWeight),
        entryTime:           entryForm.entryTime,
        driverName:          entryForm.driverName,
        driverContact:       entryForm.driverContact,
        charaType:           entryForm.charaType || null,
        entryWeightSlipUrl:  slipUrl,
        vehicleImageUrl:     vehicleImgUrl,
      });
      setEntries(p => [mapRow(data), ...p]);
      setEntryModal(false);
      setEntryForm({ date:'', vehicleNo:'', vehicleWeight:'', entryTime:'', driverName:'', driverContact:'', charaType:'', entryWeightSlip:null, vehicleImage:null });
    } catch (err) { alert('Error: ' + err.message); }
    setSubmitting(false);
  };

  /* ── exit submit ────────────────────────────────────────── */
  const handleExitSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    const target = entries.find(en => en.vehicleNo === exitForm.exitVehicleNo && !en.exited);
    if (!target?.id) { alert('Entry not found. Please refresh.'); setSubmitting(false); return; }
    try {
      const slipUrl = await uploadSlip(exitForm.exitWeightSlip, 'exit');
      await submitVehicleExit(target.id, {
        exitDate:            exitForm.exitDate,
        exitTime:            exitForm.exitTime,
        exitVehicleWeight:   parseFloat(exitForm.exitVehicleWeight),
        exitWeightSlipUrl:   slipUrl,
        charaType:           exitForm.charaType || null,
      });
      setEntries(p => p.map(en => en.id === target.id
        ? { ...en, exitDate: exitForm.exitDate, exitTime: exitForm.exitTime,
            exitVehicleWeight: exitForm.exitVehicleWeight,
            exitWeightSlipUrl: slipUrl,
            charaType: exitForm.charaType || en.charaType, exited: true }
        : en));
      setExitModal(false);
      setExitForm({ exitDate:'', exitVehicleNo:'', exitTime:'', exitVehicleWeight:'', charaType:'', exitWeightSlip:null });
    } catch (err) { alert('Error: ' + err.message); }
    setSubmitting(false);
  };

  const openExitModal = (vehicleNo) => {
    const en = entries.find(e => e.vehicleNo === vehicleNo && !e.exited);
    setExitForm(p => ({ ...p, exitVehicleNo: vehicleNo, charaType: en?.charaType || '' }));
    setExitModal(true);
  };

  /* ── derived stats ──────────────────────────────────────── */
  const openVehicles  = entries.filter(e => !e.exited);
  const totalExited   = entries.filter(e => e.exited).length;
  const totalNetChara = entries.filter(e => e.exited).reduce((acc, e) => {
    const n = calcNet(e.vehicleWeight, e.exitVehicleWeight);
    return acc + (n ? parseFloat(n) : 0);
  }, 0);

  /* ── last 7 days recent table ───────────────────────────── */
  const recentEntries = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
    return entries.filter(e => new Date(e.date) >= cutoff);
  }, [entries]);

  /* ── chart data ─────────────────────────────────────────── */
  const chartData = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - chartDays);
    const map = {};
    entries
      .filter(e => e.exited && e.exitDate && new Date(e.exitDate) >= cutoff)
      .forEach(e => {
        const n = calcNet(e.vehicleWeight, e.exitVehicleWeight);
        if (n) map[e.exitDate] = (map[e.exitDate] || 0) + parseFloat(n);
      });
    return Object.entries(map)
      .sort(([a],[b]) => a.localeCompare(b))
      .map(([date, val]) => ({
        date: new Date(date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }),
        weight: parseFloat(val.toFixed(2)),
      }));
  }, [entries, chartDays]);


  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <Layout userRole={userRole} userEmail={userEmail}
      pageTitle="Dashboard" pageSubtitle="Vehicle Entry Management">

      {/* ── Stats ──────────────────────────────────────────── */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon blue"><Truck size={22} /></div>
          <div className="stat-info">
            <div className="label">Total Entries</div>
            <div className="value">{loading ? '—' : entries.length}</div>
            <div className="sub">All time</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle2 size={22} /></div>
          <div className="stat-info">
            <div className="label">Exited</div>
            <div className="value">{loading ? '—' : totalExited}</div>
            <div className="sub">Completed</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><AlertCircle size={22} /></div>
          <div className="stat-info">
            <div className="label">Open Vehicles</div>
            <div className="value">{loading ? '—' : openVehicles.length}</div>
            <div className="sub">Awaiting exit</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue"><Weight size={22} /></div>
          <div className="stat-info">
            <div className="label">Total Net Chara</div>
            <div className="value">{loading ? '—' : Math.round(totalNetChara).toLocaleString()}</div>
            <div className="sub">kg (all time)</div>
          </div>
        </div>
      </div>

      {/* ── Quick Actions ───────────────────────────────────── */}
      <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
        <div className="action-card primary-action" onClick={() => setEntryModal(true)}>
          <div className="action-icon"><Plus size={20} /></div>
          <div>
            <div className="action-label">New Vehicle Entry</div>
            <div className="action-desc">Record entry + weight slip</div>
          </div>
        </div>
        <div className="action-card" onClick={() => navigate('/reports')}>
          <div className="action-icon orange"><FileBarChart2 size={20} /></div>
          <div>
            <div className="action-label">Vehicle Reports</div>
            <div className="action-desc">Full table, filters, CSV export</div>
          </div>
        </div>
        <button className="btn-ghost" onClick={loadEntries} disabled={loading} style={{ marginLeft:'auto' }}>
          <RefreshCw size={14} style={loading ? { animation:'spin .8s linear infinite' } : {}} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Chart ──────────────────────────────────────────── */}
      <div className="section-card" style={{ marginBottom:20 }}>
        <div className="section-header">
          <div className="section-title"><Weight size={16} />Net Chara Analytics</div>
          <div className="chart-filter-tabs">
            {[7,15,30,90].map(d => (
              <button key={d} className={`chart-tab${chartDays===d?' active':''}`}
                onClick={() => setChartDays(d)}>{d} Days</button>
            ))}
          </div>
        </div>
        <div style={{ padding:'12px 8px 8px' }}>
          {chartData.length === 0
            ? <div style={{ textAlign:'center', color:'#6B7280', padding:'30px 0', fontSize:13 }}>
                No completed exits in the last {chartDays} days
              </div>
            : <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#1E5FA8" stopOpacity={0.18}/>
                      <stop offset="95%" stopColor="#1E5FA8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize:11, fill:'#6B7280' }} tickLine={false} axisLine={false}/>
                  <YAxis tick={{ fontSize:11, fill:'#6B7280' }} tickLine={false} axisLine={false}/>
                  <Tooltip content={<ChartTooltip />}/>
                  <Area type="monotone" dataKey="weight" stroke="#1E5FA8" strokeWidth={2.5}
                    fill="url(#cg)" dot={{ r:3, fill:'#1E5FA8', strokeWidth:0 }}
                    activeDot={{ r:5, fill:'#1E5FA8' }}/>
                </AreaChart>
              </ResponsiveContainer>
          }
        </div>
      </div>

      {/* ── Bottom Grid: Open Vehicles + Recent 7-Day ─────── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

        {/* Open Vehicles */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-title">
              <AlertCircle size={16} style={{ color:'#F7941D' }}/>
              Open Vehicles
              <span className="badge-pill orange" style={{ marginLeft:6 }}>
                {openVehicles.length}
              </span>
            </div>
          </div>
          <div style={{ maxHeight:320, overflowY:'auto' }}>
            {loading ? (
              <div style={{ textAlign:'center', padding:'30px 0', color:'#6B7280', fontSize:13 }}>
                Loading…
              </div>
            ) : openVehicles.length === 0 ? (
              <div style={{ textAlign:'center', padding:'30px 16px', color:'#6B7280', fontSize:13 }}>
                No open vehicles
              </div>
            ) : openVehicles.map(v => (
              <div key={v.id} style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'10px 16px', borderBottom:'1px solid #F0F4F8',
              }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700 }}>{v.vehicleNo}</div>
                  <div style={{ fontSize:12, color:'#6B7280' }}>
                    {fmtDate(v.date)} · {v.entryTime}
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280' }}>
                    {v.vehicleWeight} kg{v.charaType ? ` · ${v.charaType}` : ''}
                  </div>
                </div>
                <button className="btn-exit" onClick={() => openExitModal(v.vehicleNo)}>
                  Exit
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Last 7 Days Table */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-title">
              <FileBarChart2 size={16}/>
              Last 7 Days Entries
              <span style={{ fontSize:12, color:'#6B7280', fontWeight:500, marginLeft:6 }}>
                ({recentEntries.length})
              </span>
            </div>
          </div>
          <div style={{ overflowX:'auto', maxHeight:320, overflowY:'auto' }}>
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Vehicle No.</th>
                  <th>Chara Type</th>
                  <th>Net Chara (kg)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign:'center', padding:'24px 0', color:'#6B7280' }}>Loading…</td>
                  </tr>
                ) : recentEntries.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign:'center', padding:'24px 0', color:'#6B7280', fontSize:13 }}>
                      No entries in last 7 days
                    </td>
                  </tr>
                ) : recentEntries.map((e, i) => {
                  const net = calcNet(e.vehicleWeight, e.exitVehicleWeight);
                  return (
                    <tr key={e.id || i}>
                      <td><strong>{e.vehicleNo}</strong></td>
                      <td>
                        {e.charaType
                          ? <span className="badge-pill primary">{e.charaType}</span>
                          : <span style={{ color:'#9CA3AF' }}>—</span>}
                      </td>
                      <td>
                        {net
                          ? <strong style={{ color:'#1E5FA8' }}>{net}</strong>
                          : <span style={{ color:'#9CA3AF' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* ══ Entry Modal ════════════════════════════════════════ */}
      {entryModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEntryModal(false)}>
          <div className="modal-box">
            <div className="modal-box-header">
              <div className="modal-box-title">
                <div style={{ background:'#EBF3FF', borderRadius:8, width:34, height:34,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Truck size={18} color="#1E5FA8" />
                </div>
                New Vehicle Entry
              </div>
              <button className="modal-close" onClick={() => setEntryModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={handleEntrySubmit}>
              <div className="modal-box-body modern-form">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={entryForm.date}
                      onChange={e => setEntryForm(p=>({...p,date:e.target.value}))} required/>
                  </div>
                  <div className="form-group">
                    <label>Entry Time</label>
                    <input type="time" value={entryForm.entryTime}
                      onChange={e => setEntryForm(p=>({...p,entryTime:e.target.value}))} required/>
                  </div>
                  <div className="form-group">
                    <label>Vehicle No.</label>
                    <AutocompleteInput id="vehicleNo" value={entryForm.vehicleNo}
                      onChange={e => setEntryForm(p=>({...p,vehicleNo:e.target.value}))}
                      suggestions={vehicleNoSugs} placeholder="e.g. RJ19GA1234" required/>
                  </div>
                  <div className="form-group">
                    <label>Entry Weight (kg)</label>
                    <input type="number" step="0.01" placeholder="e.g. 14000"
                      value={entryForm.vehicleWeight}
                      onChange={e => setEntryForm(p=>({...p,vehicleWeight:e.target.value}))} required/>
                  </div>
                  <div className="form-group">
                    <label>Driver Name</label>
                    <AutocompleteInput id="driverName" value={entryForm.driverName}
                      onChange={e => setEntryForm(p=>({...p,driverName:e.target.value}))}
                      suggestions={driverNameSugs} placeholder="Full name" required/>
                  </div>
                  <div className="form-group">
                    <label>Driver Contact</label>
                    <AutocompleteInput id="driverContact" value={entryForm.driverContact}
                      onChange={e => setEntryForm(p=>({...p,driverContact:e.target.value}))}
                      suggestions={driverContactSugs} placeholder="10-digit mobile" required/>
                  </div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label>Chara Type</label>
                    <AutocompleteInput id="charaType" value={entryForm.charaType}
                      onChange={e => setEntryForm(p=>({...p,charaType:e.target.value}))}
                      suggestions={charaTypes} placeholder="Select or type chara type" minChars={0} required/>
                  </div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label>Entry Weight Slip (Image)</label>
                    <FileZone value={entryForm.entryWeightSlip}
                      onChange={f => setEntryForm(p=>({...p,entryWeightSlip:f}))} id="entrySlip" required/>
                  </div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label>Vehicle Image</label>
                    <FileZone value={entryForm.vehicleImage}
                      onChange={f => setEntryForm(p=>({...p,vehicleImage:f}))} id="vehicleImage" required/>
                  </div>
                </div>
              </div>
              <div className="modal-box-footer">
                <button type="button" className="btn-ghost" onClick={() => setEntryModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary-dash" disabled={submitting}>
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm"/> Saving…</>
                    : <><Plus size={15}/> Submit Entry</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ Exit Modal ═════════════════════════════════════════ */}
      {exitModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setExitModal(false)}>
          <div className="modal-box">
            <div className="modal-box-header">
              <div className="modal-box-title">
                <div style={{ background:'#FFF4E6', borderRadius:8, width:34, height:34,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Truck size={18} color="#F7941D" />
                </div>
                Vehicle Exit — {exitForm.exitVehicleNo}
              </div>
              <button className="modal-close" onClick={() => setExitModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={handleExitSubmit}>
              <div className="modal-box-body modern-form">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div className="form-group">
                    <label>Exit Date</label>
                    <input type="date" value={exitForm.exitDate}
                      onChange={e => setExitForm(p=>({...p,exitDate:e.target.value}))} required/>
                  </div>
                  <div className="form-group">
                    <label>Exit Time</label>
                    <input type="time" value={exitForm.exitTime}
                      onChange={e => setExitForm(p=>({...p,exitTime:e.target.value}))} required/>
                  </div>
                  <div className="form-group">
                    <label>Vehicle No.</label>
                    <input type="text" value={exitForm.exitVehicleNo} readOnly
                      style={{ padding:'10px 14px', border:'1.5px solid #E2E8F0', borderRadius:8,
                        fontSize:14, background:'#F8FAFC', cursor:'not-allowed' }}/>
                  </div>
                  <div className="form-group">
                    <label>Exit Weight (kg)</label>
                    <input type="number" step="0.01" placeholder="e.g. 8000"
                      value={exitForm.exitVehicleWeight}
                      onChange={e => setExitForm(p=>({...p,exitVehicleWeight:e.target.value}))} required/>
                  </div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label>Chara Type</label>
                    <AutocompleteInput id="charaTypeExit" value={exitForm.charaType}
                      onChange={e => setExitForm(p=>({...p,charaType:e.target.value}))}
                      suggestions={charaTypes} placeholder="Select or type chara type" minChars={0} required/>
                  </div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label>Exit Weight Slip (Image)</label>
                    <FileZone value={exitForm.exitWeightSlip}
                      onChange={f => setExitForm(p=>({...p,exitWeightSlip:f}))} id="exitSlip" required/>
                  </div>
                </div>
              </div>
              <div className="modal-box-footer">
                <button type="button" className="btn-ghost" onClick={() => setExitModal(false)}>Cancel</button>
                <button type="submit" className="btn-accent-dash" disabled={submitting}>
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm"/> Saving…</>
                    : 'Submit Exit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Homepage;
